import React, { useMemo } from 'react';
import { ICoreContext, Image, IImageData } from '@msdyn365-commerce/core';
import { SimpleProduct, Warranty, WarrantyGroup } from '@msdyn365-commerce/retail-proxy';
import classnames from 'classnames';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { makeAbsUrl } from '../../../common/utilities/makeAbsUrl';

type Props = {
    warrantyGroups?: WarrantyGroup[];
    logo?: IImageData;

    selectedWarranty: SimpleProduct | null;
    setSelectedWarranty: (warranty: SimpleProduct | null) => void;
    coreContext: ICoreContext;
};

const WarrantyOptionButton = (props: {
    warranty: Warranty;
    isSelected: boolean;
    coreContext: ICoreContext;
    onClick: (warranty: SimpleProduct | null) => void;
}) => {
    const { warranty, isSelected, coreContext, onClick } = props;
    const unit = warranty.DurationUnitValue ?? 0;
    const duration = warranty.Duration ?? 0;
    const label =
        unit === 1
            ? duration > 1
                ? 'Days'
                : 'Day'
            : unit === 2
            ? duration > 1
                ? 'Months'
                : 'Month'
            : unit === 3
            ? duration > 1
                ? 'Years'
                : 'Year'
            : '';

    const formatedPrice = useMemo(() => coreContext.cultureFormatter.formatCurrency(warranty.AdjustedPrice || warranty.BasePrice), [
        warranty,
        coreContext
    ]);

    return (
        <button
            className={classnames('msc-btn', {
                active: isSelected
            })}
            type='button'
            disabled={isSelected}
            onClick={() => onClick(warranty)}
        >
            {`${duration} ${label}`}
            <span className='warranty-price'>{formatedPrice}</span>
        </button>
    );
};

const WarrantySection = ({ warrantyGroups, logo, setSelectedWarranty, selectedWarranty, coreContext }: Props) => {
    const { actionContext } = coreContext;
    const warranties = ArrayExtensions.hasElements(warrantyGroups) ? warrantyGroups[0].Warranties : undefined;

    if (!warranties) {
        return null;
    }

    return (
        <div className='hei-buybox-warranty-section'>
            <div className='hei-buybox-warranty-options'>
                <div className='extended-life-plan'>
                    <div className='extended-life-plan-logo-title'>
                        <div className='extended-life-plan-logo'>
                            {logo && (
                                <Image
                                    {...logo}
                                    gridSettings={actionContext.requestContext.gridSettings!}
                                    imageSettings={{
                                        viewports: {
                                            xs: { q: 'w=100&m=8', w: 100, h: 0 },
                                            lg: { q: 'w=80&m=8', w: 80, h: 0 }
                                        }
                                    }}
                                    bypassHideOnFailure
                                />
                            )}
                        </div>
                        <div className='extended-life-plan-title'>Henry&apos;s Extended Life Plan</div>
                    </div>
                    <a className='extended-life-plan-link' href={makeAbsUrl(actionContext, '/henrys-extended-life-plan')}>
                        View all benefits
                    </a>
                </div>
                <div className='warranty-chooser-container'>
                    <div className='warranty-chooser'>
                        {warranties.map((w, idx) => (
                            <WarrantyOptionButton
                                key={w.ItemId ?? idx}
                                warranty={w}
                                coreContext={coreContext}
                                isSelected={w.RecordId === selectedWarranty?.RecordId}
                                onClick={w => setSelectedWarranty(w)}
                            />
                        ))}
                        <button
                            className={classnames('msc-btn', {
                                active: !selectedWarranty
                            })}
                            type='button'
                            disabled={!selectedWarranty}
                            onClick={() => setSelectedWarranty(null)}
                        >
                            {`No coverage`}
                        </button>
                    </div>
                </div>
            </div>
            <div className='mobile-extended-life-plan-link'>
                <a className='extended-life-plan-link' href={makeAbsUrl(actionContext, '/henrys-extended-life-plan')}>
                    View all benefits
                </a>
            </div>
        </div>
    );
};

export { WarrantySection };
